import { useForm, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { useAuth } from '@/components/Layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  CalendarIcon,
  Clock,
  DollarSign,
  Package,
  AlertCircle,
  ChevronDown,
  Users,
  Check,
  ChevronsUpDown,
  Plus,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState, useEffect } from 'react'

interface Teacher {
  id: number
  name: string
}

interface Student {
  id: number
  name: string
}

interface LessonPackage {
  id: number
  name: string
  lessons_count: number
}

interface StudentSubscription {
  id: number
  lesson_package_id: number
  lesson_package_name: string
  lessons_remaining: number
  expires_at: string | null
}

interface Props {
  teachers: Teacher[]
  students: Student[]
  lesson_packages: LessonPackage[]
  student_subscriptions: Record<string, StudentSubscription[]>
  occupied_times: Record<number, { start: string; end: string }[]>
  initial_date?: string
  initial_start_time?: string
}

export default function New({
  teachers,
  students,
  student_subscriptions,
  occupied_times,
  initial_date,
  initial_start_time,
}: Props) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [date, setDate] = useState<Date>(() =>
    initial_date ? new Date(initial_date + 'T00:00:00') : new Date(),
  )
  const [selectedStudentSubs, setSelectedStudentSubs] = useState<
    StudentSubscription[]
  >([])
  const [operatingHoursWarning, setOperatingHoursWarning] = useState<
    string | null
  >(null)
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([])
  const [availableTeachers, setAvailableTeachers] =
    useState<Teacher[]>(teachers)
  const [availableStudents, setAvailableStudents] =
    useState<Student[]>(students)
  const [occupiedTimes, setOccupiedTimes] = useState<
    Record<number, { start: string; end: string }[]>
  >(occupied_times || {})

  const [submitting, setSubmitting] = useState(false)
  const [studentComboboxOpen, setStudentComboboxOpen] = useState(false)
  const [extraComboboxOpen, setExtraComboboxOpen] = useState(false)
  const [extraStudents, setExtraStudents] = useState<
    { student_id: string; student_subscription_id: string }[]
  >([])
  const [mode, setMode] = useState<'availability' | 'lesson'>('availability')

  // Limpa campos ao trocar de modo
  const handleModeChange = (newMode: 'availability' | 'lesson') => {
    setMode(newMode)
    if (newMode === 'availability') {
      // Migra teacher_id para teacher_ids se houver
      if (data.teacher_id) {
        setData('teacher_ids', [data.teacher_id])
        setData('teacher_id', '')
      }
      setData('student_id', '')
      setData('student_subscription_id', '')
      setExtraStudents([])
    } else {
      // Migra o primeiro de teacher_ids para teacher_id se houver
      if ((data.teacher_ids?.length ?? 0) > 0) {
        setData('teacher_id', data.teacher_ids![0])
        setData('teacher_ids', [])
      }
    }
  }
  const { data, setData, processing, errors } = useForm({
    lesson_date: initial_date || '',
    start_time: initial_start_time || '',
    end_time: '',
    teacher_id: '',
    teacher_ids: [] as string[],
    student_id: '',
    student_subscription_id: '',
    teacher_payment: '0',
    description: '',
  })

  // Busca horários ocupados quando a data muda
  useEffect(() => {
    if (data.lesson_date) {
      fetch(`/lessons/occupied_times/${data.lesson_date}`)
        .then((response) => response.json())
        .then((data) => {
          setOccupiedTimes(data)
        })
        .catch((error) =>
          console.error('Erro ao buscar horários ocupados:', error),
        )
    }
  }, [data.lesson_date])

  // Gera todos os horários possíveis (6h às 20h, a cada hora)
  const generateAllTimeSlots = () => {
    const slots = []
    for (let hour = 6; hour < 20; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`)
    }
    return slots
  }

  // Verifica se um horário está ocupado
  const isTimeOccupied = (time: string, personId: number | string) => {
    if (!personId || !occupiedTimes[Number(personId)]) return false

    const occupiedSlots = occupiedTimes[Number(personId)]
    return occupiedSlots.some((slot) => {
      return time >= slot.start && time < slot.end
    })
  }

  // Filtra professores e alunos disponíveis baseado no horário selecionado
  const filterAvailablePeople = () => {
    if (!data.start_time || !data.lesson_date) {
      setAvailableTeachers(teachers)
      setAvailableStudents(students)
      return
    }

    // Filtra professores que NÃO estão ocupados no horário selecionado
    const freeTeachers = teachers.filter(
      (teacher) => !isTimeOccupied(data.start_time, teacher.id),
    )

    // Filtra alunos que NÃO estão ocupados no horário selecionado
    const freeStudents = students.filter(
      (student) => !isTimeOccupied(data.start_time, student.id),
    )

    setAvailableTeachers(freeTeachers)
    setAvailableStudents(freeStudents)

    // Se o professor ou professores selecionados ficaram indisponíveis, limpa a seleção
    const hasStudent = !!data.student_id
    if (hasStudent) {
      if (
        data.teacher_id &&
        !freeTeachers.find((t) => t.id.toString() === data.teacher_id)
      ) {
        setData('teacher_id', '')
      }
    } else {
      const validTeacherIds = (data.teacher_ids || []).filter((id) =>
        freeTeachers.some((t) => t.id.toString() === id),
      )
      if (validTeacherIds.length !== (data.teacher_ids || []).length) {
        setData('teacher_ids', validTeacherIds)
      }
    }
    if (
      data.student_id &&
      !freeStudents.find((s) => s.id.toString() === data.student_id)
    ) {
      setData('student_id', '')
      setData('student_subscription_id', '')
    }
  }

  // Recalcula professores e alunos disponíveis quando muda o horário
  useEffect(() => {
    filterAvailablePeople()
  }, [data.start_time, data.lesson_date, occupiedTimes])

  // Calcula horários disponíveis baseado nos professores e aluno selecionados
  const calculateAvailableSlots = () => {
    if (!data.lesson_date) {
      setAvailableTimeSlots([])
      return
    }

    const allSlots = generateAllTimeSlots()
    const hasStudent = !!data.student_id
    const teacherIds = hasStudent
      ? data.teacher_id
        ? [data.teacher_id]
        : []
      : data.teacher_ids || []

    const available = allSlots.filter((slot) => {
      // Verifica se todos os professores estão livres neste horário
      for (const teacherId of teacherIds) {
        if (isTimeOccupied(slot, teacherId)) return false
      }
      // Se tem aluno selecionado, verifica se está ocupado
      if (data.student_id && isTimeOccupied(slot, data.student_id)) {
        return false
      }
      return true
    })

    setAvailableTimeSlots(available)
  }

  // Recalcula horários disponíveis quando muda data, professor(es) ou aluno
  useEffect(() => {
    calculateAvailableSlots()
  }, [
    data.lesson_date,
    data.teacher_id,
    data.teacher_ids,
    data.student_id,
    occupiedTimes,
  ])

  // Valida se o horário está dentro do funcionamento da escola
  const validateOperatingHours = (startTime: string, lessonDate: string) => {
    if (!startTime || !lessonDate) return

    const dayOfWeek = new Date(lessonDate + 'T00:00:00').getDay()
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ]
    const dayName = dayNames[dayOfWeek]

    // Horários típicos de funcionamento (pode buscar do backend futuramente)
    const operatingHours: Record<
      string,
      { open: string; close: string } | null
    > = {
      sunday: null, // Fechado
      monday: { open: '06:00', close: '20:00' },
      tuesday: { open: '06:00', close: '20:00' },
      wednesday: { open: '06:00', close: '20:00' },
      thursday: { open: '06:00', close: '20:00' },
      friday: { open: '06:00', close: '20:00' },
      saturday: { open: '06:00', close: '20:00' },
    }

    const hours = operatingHours[dayName]

    if (!hours) {
      setOperatingHoursWarning('⚠️ A escola não funciona neste dia da semana')
      return
    }

    if (startTime < hours.open || startTime >= hours.close) {
      setOperatingHoursWarning(
        `⚠️ Horário fora do expediente. Escola funciona das ${hours.open} às ${hours.close}`,
      )
      return
    }

    setOperatingHoursWarning(null)
  }

  // Calcula automaticamente o end_time quando start_time muda (adiciona 1 hora)
  useEffect(() => {
    if (data.start_time) {
      const [hour, minute] = data.start_time.split(':').map(Number)
      const endHour = (hour + 1) % 24
      const endTime = `${endHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      setData('end_time', endTime)
    }
    // Valida horários de funcionamento
    validateOperatingHours(data.start_time, data.lesson_date)
  }, [data.start_time, data.lesson_date])

  // Atualiza as assinaturas disponíveis quando o aluno muda
  useEffect(() => {
    if (data.student_id) {
      const subs = student_subscriptions[data.student_id] || []
      setSelectedStudentSubs(subs)

      // Auto-seleciona a primeira assinatura com aulas disponíveis
      const availableSub = subs.find((s) => s.lessons_remaining > 0)
      if (availableSub) {
        setData('student_subscription_id', availableSub.id.toString())
      } else {
        setData('student_subscription_id', '')
      }
    } else {
      setSelectedStudentSubs([])
      setData('student_subscription_id', '')
    }
  }, [data.student_id])

  const studentName = (id: string) =>
    students.find((s) => s.id.toString() === id)?.name || ''

  const addExtraStudent = (id: string) => {
    if (extraStudents.some((x) => x.student_id === id)) return
    const subs = student_subscriptions[id] || []
    const firstSub = subs.find((s) => s.lessons_remaining > 0)
    setExtraStudents([
      ...extraStudents,
      {
        student_id: id,
        student_subscription_id: firstSub ? firstSub.id.toString() : '',
      },
    ])
  }

  const removeExtraStudent = (id: string) =>
    setExtraStudents(extraStudents.filter((x) => x.student_id !== id))

  const setExtraSub = (id: string, subId: string) =>
    setExtraStudents(
      extraStudents.map((x) =>
        x.student_id === id ? { ...x, student_subscription_id: subId } : x,
      ),
    )

  const availableExtraStudents = availableStudents.filter(
    (s) =>
      s.id.toString() !== data.student_id &&
      !extraStudents.some((x) => x.student_id === s.id.toString()),
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const isLesson = mode === 'lesson'
    const teacherIds = isLesson
      ? data.teacher_id
        ? [data.teacher_id]
        : []
      : data.teacher_ids || []

    if (teacherIds.length === 0) return

    const payload = {
      lesson: {
        lesson_date: data.lesson_date,
        start_time: data.start_time,
        end_time: data.end_time,
        teacher_id: isLesson ? data.teacher_id : teacherIds[0],
        ...(!isLesson && { teacher_ids: teacherIds }),
        student_id: isLesson ? data.student_id || null : null,
        description: data.description,
        teacher_payment: data.teacher_payment,
      },
      student_subscription_id: isLesson
        ? data.student_subscription_id || null
        : null,
      extra_students: isLesson && data.student_id ? extraStudents : [],
    }

    setSubmitting(true)
    router.post('/lessons', payload, {
      onFinish: () => setSubmitting(false),
    })
  }

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate || new Date())
    if (selectedDate) {
      setData('lesson_date', format(selectedDate, 'yyyy-MM-dd'))
    }
  }

  const calculateDuration = () => {
    if (data.start_time && data.end_time) {
      const [startHour, startMin] = data.start_time.split(':').map(Number)
      const [endHour, endMin] = data.end_time.split(':').map(Number)

      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      const durationMinutes = endMinutes - startMinutes

      if (durationMinutes > 0) {
        const hours = Math.floor(durationMinutes / 60)
        const minutes = durationMinutes % 60
        return `${hours}h ${minutes}min`
      }
    }
    return null
  }

  const duration = calculateDuration()

  const selectedSub = selectedStudentSubs.find(
    (s) => s.id.toString() === data.student_subscription_id,
  )
  // Permite agendar se: não tem subscription selecionada OU tem subscription com aulas disponíveis
  const canSchedule =
    !data.student_subscription_id ||
    (selectedSub && selectedSub.lessons_remaining > 0)

  // Verifica se todos os campos obrigatórios estão preenchidos
  const hasTeachers =
    mode === 'lesson' ? !!data.teacher_id : (data.teacher_ids?.length ?? 0) > 0
  const hasRequiredStudent = mode === 'lesson' ? !!data.student_id : true
  const isFormValid =
    data.lesson_date &&
    data.start_time &&
    data.end_time &&
    hasTeachers &&
    hasRequiredStudent

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-primary" />
              </div>
              {mode === 'availability'
                ? 'Disponibilizar Horário'
                : 'Agendar Aula'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Data da Aula */}
              <div className="space-y-2">
                <Label htmlFor="lesson_date">Data da Aula *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        !date && 'text-muted-foreground'
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date
                        ? format(date, 'PPP', { locale: ptBR })
                        : 'Selecione uma data'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                      locale={ptBR}
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
                {errors.lesson_date && (
                  <p className="text-sm text-destructive">
                    {errors.lesson_date}
                  </p>
                )}
              </div>

              {/* Tipo de ação */}
              <div className="space-y-4">
                <Label>O que deseja fazer? *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleModeChange('availability')}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors',
                      mode === 'availability'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-muted hover:border-muted-foreground/25',
                    )}
                  >
                    <CalendarIcon className="h-6 w-6" />
                    <div>
                      <p className="font-medium text-sm">
                        Disponibilizar horário
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Liberar vagas para alunos agendarem
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('lesson')}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors',
                      mode === 'lesson'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-muted hover:border-muted-foreground/25',
                    )}
                  >
                    <Users className="h-6 w-6" />
                    <div>
                      <p className="font-medium text-sm">Agendar aula</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Marcar aula com professor e aluno
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Professores (modo disponibilizar) */}
              {mode === 'availability' && (
                <div className="space-y-2">
                  <Label>Professores *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                      >
                        <span className="truncate">
                          {(data.teacher_ids?.length ?? 0) > 0
                            ? `${data.teacher_ids!.length} professor(es) selecionado(s)`
                            : 'Selecione os professores'}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[var(--radix-popover-trigger-width)] p-2"
                      align="start"
                    >
                      {availableTeachers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Selecione data e horário primeiro para ver professores
                          disponíveis
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {availableTeachers.map((teacher) => (
                            <label
                              key={teacher.id}
                              className="flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer"
                            >
                              <Checkbox
                                checked={(data.teacher_ids || []).includes(
                                  teacher.id.toString(),
                                )}
                                onCheckedChange={(checked) => {
                                  const ids = data.teacher_ids || []
                                  if (checked) {
                                    setData('teacher_ids', [
                                      ...ids,
                                      teacher.id.toString(),
                                    ])
                                  } else {
                                    setData(
                                      'teacher_ids',
                                      ids.filter(
                                        (id) => id !== teacher.id.toString(),
                                      ),
                                    )
                                  }
                                }}
                              />
                              <span className="text-sm">{teacher.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {(data.teacher_ids?.length ?? 0) > 0 && (
                        <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                          <Users className="w-3 h-3 inline mr-1" />
                          Serão criados horários para todos os professores
                          selecionados
                        </p>
                      )}
                    </PopoverContent>
                  </Popover>
                  {errors.teacher_id && (
                    <p className="text-sm text-destructive">
                      {errors.teacher_id}
                    </p>
                  )}
                </div>
              )}

              {/* Professor + Aluno (modo agendar aula) */}
              {mode === 'lesson' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Professor *</Label>
                    <Select
                      value={data.teacher_id}
                      onValueChange={(value) => setData('teacher_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o professor" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTeachers.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            Nenhum professor disponível neste horário
                          </div>
                        ) : (
                          availableTeachers.map((teacher) => (
                            <SelectItem
                              key={teacher.id}
                              value={teacher.id.toString()}
                            >
                              {teacher.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {errors.teacher_id && (
                      <p className="text-sm text-destructive">
                        {errors.teacher_id}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Aluno *</Label>
                    <Popover
                      open={studentComboboxOpen}
                      onOpenChange={setStudentComboboxOpen}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          role="combobox"
                          aria-expanded={studentComboboxOpen}
                          className={cn(
                            'border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs h-9',
                            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none',
                            !data.student_id && 'text-muted-foreground',
                          )}
                        >
                          <span className="truncate">
                            {data.student_id
                              ? availableStudents.find(
                                  (s) => s.id.toString() === data.student_id,
                                )?.name
                              : 'Selecione o aluno'}
                          </span>
                          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="!w-[var(--radix-popover-trigger-width)] !p-0"
                        align="start"
                      >
                        <Command>
                          <CommandInput placeholder="Buscar aluno..." />
                          <CommandList>
                            <CommandEmpty>
                              Nenhum aluno encontrado.
                            </CommandEmpty>
                            <CommandGroup>
                              {availableStudents.map((student) => (
                                <CommandItem
                                  key={student.id}
                                  value={student.name}
                                  onSelect={() => {
                                    setData(
                                      'student_id',
                                      data.student_id === student.id.toString()
                                        ? ''
                                        : student.id.toString(),
                                    )
                                    setStudentComboboxOpen(false)
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      data.student_id === student.id.toString()
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {student.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {errors.student_id && (
                      <p className="text-sm text-destructive">
                        {errors.student_id}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Pacote/Assinatura do Aluno */}
              {mode === 'lesson' && data.student_id && (
                <div className="space-y-2">
                  <Label htmlFor="student_subscription_id">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Pacote de Aulas
                    </div>
                  </Label>

                  {selectedStudentSubs.length > 0 ? (
                    <>
                      <Select
                        value={data.student_subscription_id}
                        onValueChange={(value) =>
                          setData('student_subscription_id', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um pacote" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedStudentSubs.map((sub) => (
                            <SelectItem
                              key={sub.id}
                              value={sub.id.toString()}
                              disabled={sub.lessons_remaining === 0}
                            >
                              {sub.lesson_package_name} -{' '}
                              {sub.lessons_remaining} aulas restantes
                              {sub.expires_at &&
                                ` (válido até ${format(new Date(sub.expires_at), 'dd/MM/yyyy')})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {selectedStudentSubs.every(
                        (s) => s.lessons_remaining === 0,
                      ) && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Este aluno não possui aulas disponíveis em nenhum
                            pacote ativo.
                          </AlertDescription>
                        </Alert>
                      )}

                      <p className="text-xs text-muted-foreground">
                        Ao agendar, será descontada 1 aula do pacote selecionado
                      </p>
                    </>
                  ) : (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Este aluno não possui pacotes de aulas ativos. A aula
                        será agendada sem débito de pacote.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Outros alunos (mesmo professor, mesma aula) */}
              {mode === 'lesson' && data.student_id && (
                <div className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    <Label className="m-0">Outros alunos nesta aula</Label>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-1">
                    O professor dará esta aula para mais de um aluno. Cada aluno
                    extra desconta 1 aula do próprio pacote.
                  </p>

                  {extraStudents.map((extra) => {
                    const subs = student_subscriptions[extra.student_id] || []
                    return (
                      <div
                        key={extra.student_id}
                        className="flex flex-col gap-2 rounded-md bg-muted/50 p-3 sm:flex-row sm:items-center"
                      >
                        <span className="flex-1 text-sm font-medium">
                          {studentName(extra.student_id)}
                        </span>
                        {subs.length > 0 ? (
                          <Select
                            value={extra.student_subscription_id}
                            onValueChange={(value) =>
                              setExtraSub(extra.student_id, value)
                            }
                          >
                            <SelectTrigger className="sm:w-72">
                              <SelectValue placeholder="Selecione o pacote" />
                            </SelectTrigger>
                            <SelectContent>
                              {subs.map((sub) => (
                                <SelectItem
                                  key={sub.id}
                                  value={sub.id.toString()}
                                  disabled={sub.lessons_remaining <= 0}
                                >
                                  {sub.lesson_package_name} -{' '}
                                  {sub.lessons_remaining} aulas restantes
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-muted-foreground sm:w-72">
                            Sem pacote ativo (sem débito)
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeExtraStudent(extra.student_id)}
                          aria-label="Remover aluno"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}

                  <Popover
                    open={extraComboboxOpen}
                    onOpenChange={setExtraComboboxOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Adicionar aluno
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="!w-[var(--radix-popover-trigger-width)] !p-0"
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder="Buscar aluno..." />
                        <CommandList>
                          <CommandEmpty>Nenhum aluno disponível.</CommandEmpty>
                          <CommandGroup>
                            {availableExtraStudents.map((student) => (
                              <CommandItem
                                key={student.id}
                                value={student.name}
                                onSelect={() => {
                                  addExtraStudent(student.id.toString())
                                  setExtraComboboxOpen(false)
                                }}
                              >
                                {student.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Horários */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time">Horário de Início *</Label>
                  <Select
                    value={data.start_time}
                    onValueChange={(value) => setData('start_time', value)}
                    disabled={!data.lesson_date || !hasTeachers}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !data.lesson_date
                            ? 'Selecione uma data primeiro'
                            : !hasTeachers
                              ? 'Selecione o(s) professor(es)'
                              : availableTimeSlots.length === 0
                                ? 'Sem horários disponíveis'
                                : 'Selecione o horário'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.start_time && (
                    <p className="text-sm text-destructive">
                      {errors.start_time}
                    </p>
                  )}
                  {data.lesson_date &&
                    hasTeachers &&
                    availableTimeSlots.length === 0 && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Não há horários disponíveis. Professor
                          {data.student_id ? ' ou aluno' : ''} já possui aulas
                          em todos os horários.
                        </AlertDescription>
                      </Alert>
                    )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_time">Horário de Término *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="end_time"
                      type="time"
                      className="pl-10"
                      value={data.end_time}
                      onChange={(e) => setData('end_time', e.target.value)}
                    />
                  </div>
                  {errors.end_time && (
                    <p className="text-sm text-destructive">
                      {errors.end_time}
                    </p>
                  )}
                </div>
              </div>

              {/* Duração Calculada */}
              {duration && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm font-medium">
                    Duração: <span className="text-primary">{duration}</span>
                  </p>
                </div>
              )}

              {/* Valor do Professor — só o dono. O que a escola paga a cada professor
                não é informação de quem só administra a agenda, e o servidor ignora
                o campo se ele vier assim mesmo. */}
              {isOwner && (
                <div className="space-y-2">
                  <Label htmlFor="teacher_payment">
                    Valor para o Professor
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="teacher_payment"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-10"
                      value={data.teacher_payment}
                      onChange={(e) =>
                        setData('teacher_payment', e.target.value)
                      }
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Valor que será pago ao professor por esta aula (opcional)
                  </p>
                  {errors.teacher_payment && (
                    <p className="text-sm text-destructive">
                      {errors.teacher_payment}
                    </p>
                  )}
                </div>
              )}

              {/* Alerta de horário fora do expediente */}
              {operatingHoursWarning && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{operatingHoursWarning}</AlertDescription>
                </Alert>
              )}

              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="description">Observações</Label>
                <Textarea
                  id="description"
                  placeholder="Adicione observações sobre a aula..."
                  rows={3}
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.history.back()}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={
                    processing || submitting || !canSchedule || !isFormValid
                  }
                >
                  {processing || submitting
                    ? mode === 'availability'
                      ? 'Disponibilizando...'
                      : 'Agendando...'
                    : mode === 'availability'
                      ? 'Disponibilizar Horário'
                      : 'Agendar Aula'}
                </Button>
              </div>

              {!isFormValid && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {mode === 'availability'
                      ? 'Preencha todos os campos obrigatórios: Data, Horários e Professor(es)'
                      : 'Preencha todos os campos obrigatórios: Data, Horários, Professor e Aluno'}
                  </AlertDescription>
                </Alert>
              )}

              {!canSchedule &&
                data.student_id &&
                data.student_subscription_id && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      O pacote selecionado não possui aulas disponíveis
                    </AlertDescription>
                  </Alert>
                )}
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
